/*
 * Countersigning - the buyer's half, which is us.
 *
 * The seller signs from their own link and never sees this. What they signed is
 * shown here first, because countersigning without reading what you are putting
 * your name to is the failure this panel exists to prevent: the contract the
 * seller signed is opened from the same file the seller's signature was filed
 * against, not from a fresh rendering of the current deal record.
 *
 * The signature itself is drawn, not typed. A typed name alone is a claim; a
 * drawing plus the account it came from is a record.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import { Deal } from '../../types'
import { supabase } from '../../lib/supabase'
import { apiHeadersOrThrow } from '../../lib/authHeader'
import './CounterSignPanel.css'

interface Props {
  deal: Deal
  onSaved: (msg: string) => void
}

interface BosRow {
  id: string
  bos_pdf_path: string | null
  seller_signed_at: string | null
  seller_signed_by_name: string | null
  buyer_signed_at: string | null
  buyer_signed_by_name: string | null
  executed_pdf_path: string | null
  executed_pdf_sha256: string | null
}

/* Matches the pad the seller signs on, so both signatures render alike. */
const PAD_W = 518
const PAD_H = 170

function when(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function CounterSignPanel({ deal, onSaved }: Props) {
  const [row, setRow] = useState<BosRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [typedName, setTypedName] = useState('')

  /*
   * Winnie, 10 Oct: "Your Full Name: should pick up the sale representative user's full
   * name (person who created the deal)."
   *
   * On 1 Sep this defaulted to the signed-in user instead, on the reasoning that the
   * person signing might not be the one who created the deal. In practice the field
   * came up empty: the name was read from the login record's metadata, which most
   * accounts do not carry. It now reads the deal creator's name from Users &
   * Permissions, which is what Winnie asked for twice.
   *
   * If that cannot be read (an older deal with no creator recorded, or an account that
   * may only see its own profile), it falls back to the signed-in user's own profile,
   * then to the login metadata. The field stays editable, so anyone else countersigning
   * corrects it to their own name before signing - the executed contract records what
   * is in the box, and the account that signed is recorded alongside it regardless.
   *
   * Only fills an empty field, so it never overwrites something already typed.
   */
  useEffect(() => {
    let cancelled = false
    const fill = (name: string) => {
      if (cancelled || !name) return
      setTypedName((prev) => (prev ? prev : name))
    }
    const profileName = async (id: string): Promise<string> => {
      const { data } = await supabase.from('user_profiles').select('full_name').eq('id', id).maybeSingle()
      const n = data && typeof (data as { full_name?: unknown }).full_name === 'string'
        ? String((data as { full_name: string }).full_name).trim()
        : ''
      return n
    }
    ;(async () => {
      const creatorId = (deal as { user_id?: string | null }).user_id
      if (creatorId) {
        const creator = await profileName(creatorId)
        if (creator) { fill(creator); return }
      }
      const { data } = await supabase.auth.getUser()
      const u = data?.user
      if (!u) return
      const own = await profileName(u.id)
      if (own) { fill(own); return }
      const meta = (u.user_metadata || {}) as Record<string, unknown>
      fill(
        (typeof meta.full_name === 'string' && meta.full_name.trim()) ||
        (typeof meta.name === 'string' && meta.name.trim()) ||
        ''
      )
    })()
    return () => {
      cancelled = true
    }
  }, [deal])
  const [strokes, setStrokes] = useState<number[][][]>([])

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const current = useRef<number[][]>([])

  const load = useCallback(async () => {
    setLoading(true)
    const { data, error: err } = await supabase
      .from('bill_of_sale_documents')
      .select('id, bos_pdf_path, seller_signed_at, seller_signed_by_name, buyer_signed_at, buyer_signed_by_name, executed_pdf_path, executed_pdf_sha256')
      .eq('deal_id', deal.id)
      .order('created_at', { ascending: false })
      .limit(1)
    setRow(!err && data && data.length ? (data[0] as BosRow) : null)
    setLoading(false)
  }, [deal.id])

  useEffect(() => { load() }, [load])


  /* Redraw whatever has been drawn so far. */
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, PAD_W, PAD_H)
    ctx.lineWidth = 2.2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#111'
    const all = current.current.length ? strokes.concat([current.current]) : strokes
    all.forEach((stroke) => {
      if (stroke.length < 2) return
      ctx.beginPath()
      ctx.moveTo(stroke[0][0], stroke[0][1])
      for (let i = 1; i < stroke.length; i += 1) ctx.lineTo(stroke[i][0], stroke[i][1])
      ctx.stroke()
    })
  }, [strokes])

  const pointFrom = (e: ReactPointerEvent<HTMLCanvasElement>): number[] => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * PAD_W
    const y = ((e.clientY - rect.top) / rect.height) * PAD_H
    return [Math.round(x * 10) / 10, Math.round(y * 10) / 10]
  }

  const start = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    drawing.current = true
    current.current = [pointFrom(e)]
  }

  const move = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    current.current.push(pointFrom(e))
    setStrokes((s) => s.slice())
  }

  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    const stroke = current.current
    current.current = []
    if (stroke.length > 1) setStrokes((s) => s.concat([stroke]))
    else setStrokes((s) => s.slice())
  }

  const clear = () => {
    current.current = []
    setStrokes([])
    setError(null)
  }

  const toSvg = (): string => {
    const d = strokes
      .filter((stroke) => stroke.length > 1)
      /*
       * 'M x y L x y' with the spaces, matching the pad the seller signs on.
       * The renderer reads both dialects now, but two pads writing the same
       * thing two ways is how that went unnoticed in the first place.
       */
      .map((stroke) => 'M ' + stroke.map((p) => p[0] + ' ' + p[1]).join(' L '))
      .join(' ')
    if (!d) return ''
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + PAD_W + ' ' + PAD_H +
      '" width="' + PAD_W + '" height="' + PAD_H + '"><path d="' + d +
      '" fill="none" stroke="#111" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  }

  const open = async (path: string | null) => {
    if (!path) return
    const { data, error: err } = await supabase.storage.from('bill-of-sale').createSignedUrl(path, 120)
    if (err || !data || !data.signedUrl) {
      setError('Could not open that document just now. It is still on file.')
      return
    }
    window.open(data.signedUrl, '_blank', 'noopener,noreferrer')
  }

  /*
   * Produce the executed PDF again from the signature already recorded.
   *
   * Nothing about who signed or when is touched. This exists because the
   * document is only a rendering, and a rendering can be wrong or missing
   * while the signature behind it is perfectly good.
   */
  const refile = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/countersign', {
        method: 'POST',
        headers: await apiHeadersOrThrow(),
        body: JSON.stringify({ dealId: deal.id, refile: true }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok || !data || !data.success) {
        setError((data && data.error) || 'That did not go through. Nothing was changed.')
        setBusy(false)
        return
      }
      onSaved('The executed copy has been filed again.')
      await load()
    } catch (err) {
      setError('That did not go through. Nothing was changed.')
    }
    setBusy(false)
  }

  const countersign = async () => {
    const name = typedName.trim().replace(/\s+/g, ' ')
    const svg = toSvg()
    if (!name) { setError('Please type the name you are signing under.'); return }
    if (!svg) { setError('Please draw your signature in the box.'); return }
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/countersign', {
        method: 'POST',
        headers: await apiHeadersOrThrow(),
        body: JSON.stringify({ dealId: deal.id, typedName: name, signatureSvg: svg }),
      })
      const data = await res.json().catch(() => null)
      if (!res.ok || !data || !data.success) {
        setError((data && data.error) || 'That did not go through. Nothing was changed.')
        setBusy(false)
        return
      }
      /* Said plainly: the signature is safe even when the copy is not filed. */
      onSaved(data.fileNote || 'Contract executed. Both signatures are on file.')
      clear()
      await load()
    } catch (err) {
      setError('That did not go through. Nothing was changed.')
    }
    setBusy(false)
  }

  if (loading) return <div className="cs-panel"><p className="cs-quiet">Reading the contract...</p></div>

  if (!row) {
    return (
      <div className="cs-panel">
        <h3 className="cs-head">Countersign</h3>
        <p className="cs-quiet">There is no Vehicle Purchase Agreement on this deal yet.</p>
      </div>
    )
  }

  if (!row.seller_signed_at) {
    return (
      <div className="cs-panel">
        <h3 className="cs-head">Countersign</h3>
        <p className="cs-quiet">
          The seller has not signed yet, so there is nothing to countersign. This opens once they have.
        </p>
      </div>
    )
  }

  const executed = Boolean(row.buyer_signed_at)

  return (
    <div className="cs-panel">
      <h3 className="cs-head">{executed ? 'Executed contract' : 'Countersign'}</h3>

      <div className="cs-rows">
        <div className="cs-row">
          <span className="cs-k">Seller signed</span>
          <span className="cs-v">{row.seller_signed_by_name || 'name not recorded'} - {when(row.seller_signed_at)}</span>
        </div>
        {executed ? (
          <div className="cs-row">
            <span className="cs-k">Countersigned</span>
            <span className="cs-v">{row.buyer_signed_by_name || 'name not recorded'} - {when(row.buyer_signed_at)}</span>
          </div>
        ) : null}
        {executed && row.executed_pdf_sha256 ? (
          <div className="cs-row">
            <span className="cs-k">Executed hash</span>
            <span className="cs-v cs-hash">{row.executed_pdf_sha256}</span>
          </div>
        ) : null}
      </div>

      <div className="cs-actions">
        <button type="button" onClick={() => open(row.bos_pdf_path)} disabled={!row.bos_pdf_path}>
          View Seller signed Vehicle Purchase Agreement
        </button>
        {executed ? (
          <>
          <button type="button" onClick={() => open(row.executed_pdf_path)} disabled={!row.executed_pdf_path}>
            Open the executed contract
          </button>
          <button type="button" onClick={refile} disabled={busy}>
            {busy ? 'Filing...' : 'File the executed copy again'}
          </button>
          </>
        ) : null}
      </div>

      {error ? <p className="cs-alert">{error}</p> : null}

      {executed ? (
        <p className="cs-quiet">
          This contract is executed. The seller-signed original is kept separately and unchanged, so
          both what they agreed to and what was executed can be produced.
        </p>
      ) : (
        <>
          <p className="cs-quiet">
            Read the seller-signed contract above before you sign. Once countersigned it is executed,
            and correcting it is a deliberate act with its own record.
          </p>

          <label className="cs-label" htmlFor="cs-name">Your full name</label>
          <input
            id="cs-name"
            className="cs-input"
            type="text"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder="The name you are signing under"
          />

          <span className="cs-label">Draw your signature</span>
          <canvas
            ref={canvasRef}
            className="cs-pad"
            width={PAD_W}
            height={PAD_H}
            onPointerDown={start}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={end}
            onPointerLeave={end}
          />

          <div className="cs-actions">
            <button type="button" className="cs-clear" onClick={clear} disabled={busy}>
              Clear
            </button>
            <button type="button" className="cs-go" onClick={countersign} disabled={busy}>
              {busy ? 'Signing...' : 'Countersign and execute'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
